/* eslint-disable @typescript-eslint/ban-types */

// vendor modules
import React, { memo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import axios from "axios"

// components
import Inline from "shared/search/containers/Inline/index"
import GenericInlinePresentation from "search-new/components/Inline/LongInlinePresentation"
import SquareInlinePresentation from "search-new/components/Inline/SquareInlinePresentation"

import personInlineSelector from "shared/search/inline-selectors/personInlineSelectors"
import documentInlineSelector from "shared/search/inline-selectors/documentInlineSelectors"
import { selectAlignmentTypesAsEnum } from "shared/stakeholder/selectors"
import { selectFirstLedgerSettingsId } from "shared/pac/selectors"
import { RECEIPT_ENDPOINT, DISBURSEMENT_ENDPOINT } from "shared/djangio/moneyballHelpers"
import {
    segueToEditBill,
    segueToEditComment,
    segueToEditContact,
    segueToEditCustomOfficial,
    segueToEditDonation,
    segueToEditEvent,
    segueToEditInteraction,
    segueToEditNote,
    segueToEditOfficial,
    segueToEditOrganization,
    segueToEditPoliticalCommittee,
    segueToEditPressContact,
    segueToEditRegulation,
    segueToEditRelationship,
    segueToModifyOrganization,
    segueToOutboxCompose,
    segueToOutboxComposeSupporter,
} from "app/static/frontend/global-action-creators"
import { setOrCreatePriority, setOrCreateStance } from "shared/profiles/position/action-creators"
import { postProfileAlignment } from "shared/stakeholder/action-creators"
import { triggerDataRefresh } from "shared/search/action-creators"

import { requestChanges as pressContactRequestChanges } from "app/static/frontend/profiles-new/routes/press-contact/action-creators"
import { requestChanges as officialStafferRequestChanges } from "app/static/frontend/profiles-new/routes/official/action-creators"

import { createNewTemplateFromSearch } from "app/static/frontend/search-new/containers/Downloads/new-download-action-creators"

import {
    deleteContact,
    deleteEvent,
    deleteFile,
    deleteGrassrootsAction,
    deleteNote,
    deletePublicOrganization,
    updateFile,
    updateInlineAppearance,
    updateSupporterAction,
} from "app/static/frontend/search-new/action-creators"

// selectors
import {
    agencyInlineSelector,
    alertGroupInlineSelector,
    alertInlineSelector,
    amendmentInlineSelector,
    articleInlineSelector,
    articleTopicInlineSelector,
    attachmentInlineSelector,
    billInlineSelector,
    bulkUploadFileInlineSelector,
    caucusInlineSelector,
    committeeInlineSelector,
    customEventParticipantInlineSelector,
    docketInlineSelector,
    eventInlineSelector,
    feedItemInlineSelector,
    fileInlineSelector,
    genericSelectorCreator,
    genericStateSelectorCreator,
    grassrootsActionInlineSelector,
    noteInlineSelector,
    personRoleInlineSelector,
    publicOrganizationInlineSelector,
    regulationInlineSelector,
    regulationCommentInlineSelector,
    statInlineSelector,
    supporterInlineSelector,
    trackingDashboardInlineSelector,
    userInlineSelector,
    voteInlineSelector,
    voteResultInlineSelector,
    // searchify only
    actionCenterSettingsInlineSelector,
    assignmentInlineSelector,
    bulkEmailInlineSelector,
    bulkSMSInlineSelector,
    campaignInlineSelector,
    customDataInlineSelector,
    issueInlineSelector,
    legislativeSessionInlineSelector,
    sendEmailInlineSelector,
    sendSMSInlineSelector,
    replyEmailInlineSelector,
    stafferTermInlineSelector,
    stafferTitleInlineSelector,
    teamInlineSelector,
    // Event profile only
    eventParticipantInlineSelector,
    // Live transcript only,
    liveTranscriptSectionInlineSelector,
    issueManagementInlineSelector,
    newListInlineSelector,
    geoshapeInlineSelector,
    politicalCommitteeInlineSelector,
    transactionInlineSelector,
    pacClassicPacOfficeInlineSelector,
    pacClassicPacOrganizationInlineSelector,
    pacClassicPacTransactionInlineSelector,
    electionInlineSelector,
    beatInlineSelector,
    campaignFinanceReceiptInlineSelector,
    campaignFinanceReceiptDisabledInlineSelector,
    campaignFinanceDisbursementInlineSelector,
    campaignFinanceDisbursementDisabledInlineSelector,
} from "shared/search/inline-selectors/inline-selectors"
import {
    amendmentOptionsSelector,
    articleOptionsSelector,
    billOptionsSelector,
    campaignFinanceDisbursementOptionsSelector,
    campaignFinanceReceiptOptionsSelector,
    docketOptionsSelector,
    documentOptionsSelector,
    eventOptionsSelector,
    feedItemOptionsSelector,
    fileOptionsSelector,
    liveTranscriptSectionOptionsSelector,
    noteOptionsSelector,
    officialOptionsSelector,
    organizationOptionsSelector,
    politicalCommitteeOptionsSelector,
    pressContactOptionsSelector,
    regulationOptionsSelector,
    supporterOptionsSelector,
    transactionOptionsSelector,
    pacClassicPacTransactionOptionsSelector,
    voteOptionsSelector,
} from "shared/search/inline-selectors/inline-options-selectors"

import { inlineConnection } from "shared/search/inline-selectors/appearance-selectors"

import { selectIsInlineSearchifySelected } from "shared/search/searchify/selectors"
import {
    selectIsInlineBulkActionsSelected,
    selectIsBulkActionsSubmittable,
} from "shared/search/searchify/bulkactions/selectors"
import { getTransactionEditUrl } from "shared/pac/helperFunctions"
import { mobileBreakpoint } from "app/static/frontend/design-constants"

import { useResponsiveSize } from "@/hooks"

import paths from "app/static/frontend/imports/desktopPaths"

// New components
import { MobileAlertCard } from "@/components/MobileAlertCard"

const genericInlineConnection = (state, props) => ({
    isInlineSelected: selectIsInlineSearchifySelected(state, props) || selectIsInlineBulkActionsSelected(state, props),
    isBulkActions: selectIsBulkActionsSubmittable(state, props),
    ...(props.quorumDataType &&
    (props.quorumDataType.value === DjangIO.app.models.QuorumDataType.person.value ||
        props.quorumDataType.value === DjangIO.app.models.QuorumDataType.staffer.value ||
        props.quorumDataType.value === DjangIO.app.models.QuorumDataType.supporter.value ||
        props.quorumDataType.value === DjangIO.app.models.QuorumDataType.alert.value)
        ? { alignmentTypes: selectAlignmentTypesAsEnum(state, props) }
        : {}),
    ...(props.quorumDataType &&
    props.quorumDataType.value === DjangIO.app.models.QuorumDataType.political_committee.value
        ? { ledgerSettingsId: selectFirstLedgerSettingsId(state, props) }
        : {}),
})

const GenericInlineComponent = (props) => {
    const ConnectedGenericInline = connect(inlineConnection, {
        updateInlineAppearance,
        ...props.actionCreators,
    })(Inline)
    const inlineId = props.datum && props.datum.get && props.datum.get("id")

    const { isMobile } = useResponsiveSize()

    return inlineId ? (
        <ConnectedGenericInline
            bylineId={`byline-${inlineId}`}
            datum={props.datum}
            descriptionId={`description-${inlineId}`}
            formatSelect={props.formatSelect}
            inlineId={inlineId}
            presentationComponent={props.presentationComponent}
            selectOptions={props.selectOptions}
            titleId={`title-${inlineId}`}
            isMobile={isMobile}
            {...props}
        />
    ) : null
}
GenericInlineComponent.propTypes = {
    datum: PropTypes.object,
    descriptionId: PropTypes.string,
    titleId: PropTypes.string,
    bylineId: PropTypes.string,
    formatSelect: PropTypes.func,
    inlineId: PropTypes.number,
    presentationComponent: PropTypes.func,
    selectOptions: PropTypes.func,
    quorumDataType: PropTypes.object,
    renderCustomFields: PropTypes.bool,
    actionCreators: PropTypes.object,
}
// Necessary because passing an object of action creators causes a re-render
// Ensures that infinite scroll events in search are O(1) rather than O(n)
// Will not prevent the select option buttons for working as that re-render event is handled
// in the respective Long/SquareInlinePresentation components
const shouldSkipRerender = (oldProps, newProps) => {
    return newProps.isInlineSelected === oldProps.isInlineSelected && newProps.isBulkActions === oldProps.isBulkActions
}
export const GenericInline = connect(genericInlineConnection, null)(memo(GenericInlineComponent, shouldSkipRerender))

export const UserInline = (props) => (
    <GenericInline
        formatSelect={userInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.user}
        {...props}
    />
)

export const NewListInline = (props) => (
    <GenericInline
        formatSelect={newListInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.new_list}
        {...props}
    />
)

export const OfficialInline = (props) => {
    const isMobile = window.innerWidth <= mobileBreakpoint
    return (
        <GenericInline
            formatSelect={personInlineSelector}
            presentationComponent={isMobile ? GenericInlinePresentation : SquareInlinePresentation}
            quorumDataType={DjangIO.app.models.QuorumDataType.person}
            selectOptions={officialOptionsSelector}
            actionCreators={{
                postProfileAlignment,
                requestChanges: officialStafferRequestChanges,
                segueToEditCustomOfficial,
                segueToEditOfficial,
                segueToOutboxCompose,
                setOrCreatePriority,
            }}
            {...props}
        />
    )
}

export const StafferInline = (props) => {
    const isMobile = window.innerWidth <= mobileBreakpoint
    return (
        <GenericInline
            formatSelect={personInlineSelector}
            presentationComponent={isMobile ? GenericInlinePresentation : SquareInlinePresentation}
            quorumDataType={DjangIO.app.models.QuorumDataType.staffer}
            selectOptions={officialOptionsSelector}
            actionCreators={{
                postProfileAlignment,
                segueToEditOfficial,
                segueToOutboxCompose,
                setOrCreatePriority,
                requestChanges: officialStafferRequestChanges,
            }}
            {...props}
        />
    )
}

export const PressContactInline = (props) => {
    const isMobile = window.innerWidth <= mobileBreakpoint
    return (
        <GenericInline
            formatSelect={personInlineSelector}
            presentationComponent={isMobile ? GenericInlinePresentation : SquareInlinePresentation}
            quorumDataType={DjangIO.app.models.QuorumDataType.press_contact}
            selectOptions={pressContactOptionsSelector}
            actionCreators={{
                segueToEditPressContact,
                segueToOutboxCompose,
                requestChanges: pressContactRequestChanges,
            }}
            {...props}
        />
    )
}

export const BillInline = (props) => (
    <GenericInline
        formatSelect={billInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.bill}
        selectOptions={billOptionsSelector}
        actionCreators={{
            segueToEditBill,
            setOrCreatePriority,
            setOrCreateStance,
        }}
        {...props}
    />
)

export const DocumentInline = (props) => (
    <GenericInline
        formatSelect={documentInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.document}
        selectOptions={documentOptionsSelector}
        customOnPress={(id, inlineProps) => {
            const documentType = props.datum.get("document_type")
            const liveTranscriptType = DjangIO.app.document.models.DocumentType.live_transcript_document
            const committeeTranscriptType = DjangIO.app.document.models.DocumentType.committee_transcript
            const verifiedTranscriptType = DjangIO.app.document.models.DocumentType.manual_committee_transcript
            const usPresidentTranscript = DjangIO.app.document.models.DocumentType.us_president_transcript
            const stateFloorTranscript = DjangIO.app.document.models.DocumentType.state_floor_transcript
            const localMeetingTranscript = DjangIO.app.document.models.DocumentType.local_meeting_transcript

            if (documentType && documentType === liveTranscriptType.value) {
                inlineProps.segueToProfile(
                    { id: props.datum.get("related_event") },
                    DjangIO.app.models.QuorumDataType.internal_cspan_event.value,
                )
            } else if (
                documentType &&
                [
                    committeeTranscriptType.value,
                    verifiedTranscriptType.value,
                    usPresidentTranscript.value,
                    stateFloorTranscript.value,
                    localMeetingTranscript.value,
                ].includes(documentType)
            ) {
                inlineProps.segueToProfile(
                    {
                        id: props.datum.get("committee_meeting_id"),
                        extraUrlParameters: `transcript/?sequence=${props.datum.get("sequence")}`,
                    },
                    DjangIO.app.models.QuorumDataType.event.value,
                )
            } else {
                inlineProps.segueToProfile({ id, fromSearch: true }, inlineProps.quorumDataType.value)
            }
        }}
        actionCreators={{
            setOrCreateStance,
        }}
        {...props}
    />
)

export const SupporterInline = (props) => {
    return (
        <GenericInline
            formatSelect={supporterInlineSelector}
            presentationComponent={
                window.innerWidth <= mobileBreakpoint ? GenericInlinePresentation : SquareInlinePresentation
            }
            quorumDataType={DjangIO.app.models.QuorumDataType.supporter}
            selectOptions={supporterOptionsSelector}
            actionCreators={{
                deleteContact,
                postProfileAlignment,
                segueToEditContact,
                segueToOutboxComposeSupporter,
                setOrCreatePriority,
            }}
            {...props}
        />
    )
}

export const PublicOrganizationInline = (props) => (
    <GenericInline
        formatSelect={publicOrganizationInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.public_organization}
        selectOptions={organizationOptionsSelector}
        actionCreators={{
            segueToEditOrganization,
            deletePublicOrganization,
            segueToModifyOrganization,
        }}
        {...props}
    />
)

export const IssueManagementInline = (props) => (
    <GenericInline
        formatSelect={issueManagementInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.issue_management}
        {...props}
    />
)

export const AttachmentInline = (props) => (
    <GenericInline
        formatSelect={attachmentInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.attachment}
        {...props}
    />
)

export const NoteInline = (props) => (
    <GenericInline
        alwaysHasExpandControl={true}
        formatSelect={noteInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.note}
        disableFeedback={props.datum.get("note_type") !== DjangIO.app.userdata.types.NoteType.interaction.value}
        selectOptions={noteOptionsSelector}
        renderCustomFields={true}
        actionCreators={{
            createNewTemplateFromSearch,
            deleteNote,
            segueToEditNote,
            segueToEditInteraction,
            segueToEditDonation,
            segueToEditRelationship,
            segueToEditComment,
        }}
        {...props}
    />
)

export const GrassrootsActionInline = (props) => {
    const uniqueFlvId = props.uniqueFlvId
    const grassrootsActionInlineSelectorWithFlvId = (datum) => grassrootsActionInlineSelector(datum, uniqueFlvId)

    return (
        <GenericInline
            formatSelect={grassrootsActionInlineSelectorWithFlvId}
            presentationComponent={GenericInlinePresentation}
            quorumDataType={DjangIO.app.models.QuorumDataType.grassroots_action}
            selectOptions={noteOptionsSelector}
            actionCreators={{
                createNewTemplateFromSearch,
                deleteGrassrootsAction,
                updateSupporterAction,
            }}
            {...props}
        />
    )
}

export const EventInline = (props) => (
    <GenericInline
        formatSelect={eventInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.event}
        selectOptions={eventOptionsSelector}
        actionCreators={{
            deleteEvent,
            segueToEditEvent,
        }}
        {...props}
    />
)

export const VoteInline = (props) => (
    <GenericInline
        formatSelect={voteInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.vote}
        selectOptions={voteOptionsSelector}
        actionCreators={{
            setOrCreateStance,
        }}
        {...props}
    />
)

export const AmendmentInline = (props) => (
    <GenericInline
        formatSelect={amendmentInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.amendment}
        selectOptions={amendmentOptionsSelector}
        {...props}
    />
)

export const CommitteeInline = (props) => (
    <GenericInline
        formatSelect={committeeInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.committee}
        {...props}
    />
)

export const CaucusInline = (props) => (
    <GenericInline
        formatSelect={caucusInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.caucus}
        {...props}
    />
)

export const FileInline = (props) => (
    <GenericInline
        formatSelect={fileInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.attachment}
        selectOptions={fileOptionsSelector}
        actionCreators={{
            deleteFile,
            updateFile,
            triggerDataRefresh,
        }}
        {...props}
    />
)

export const LiveTranscriptInline = (props) => (
    <GenericInline
        formatSelect={eventInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.live_transcript_document}
        {...props}
    />
)

export const RegulationInline = (props) => (
    <GenericInline
        formatSelect={regulationInlineSelector}
        presentationComponent={GenericInlinePresentation}
        selectOptions={regulationOptionsSelector}
        quorumDataType={DjangIO.app.models.QuorumDataType.regulation}
        actionCreators={{
            segueToEditRegulation,
            setOrCreatePriority,
            setOrCreateStance,
        }}
        {...props}
    />
)

export const RegulationCommentInline = (props) => (
    <GenericInline
        formatSelect={regulationCommentInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.reg_comment}
        {...props}
    />
)

export const VoteResultInline = (props) => (
    <GenericInline
        formatSelect={voteResultInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const PersonRoleInline = (props) => (
    <GenericInline
        formatSelect={personRoleInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const AlertInline = (props) => (
    <GenericInline formatSelect={alertInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const AlertGroupInline = (props) => (
    <GenericInline
        formatSelect={alertGroupInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const FeedItemInline = (props) => {
    const { isMobile } = useResponsiveSize()
    return (
        <GenericInline
            formatSelect={feedItemInlineSelector}
            presentationComponent={isMobile ? MobileAlertCard : GenericInlinePresentation}
            quorumDataType={DjangIO.app.models.QuorumDataType.alert}
            selectOptions={feedItemOptionsSelector}
            customOnPress={(id, inlineProps) => {
                const quorumLink = props.datum.get("big_cache") && props.datum.get("big_cache").get("quorum_link")
                if (quorumLink) {
                    const feedItemUrl = quorumLink.replace("https://www.quorum.us", "")
                    let extraChar = ""

                    if (
                        // do not break external links
                        quorumLink.includes("https://www.quorum.us") &&
                        // the last char is not '/' (breaks big_cache Public Organization field segue)
                        quorumLink.length &&
                        quorumLink[quorumLink.length - 1] !== "/" &&
                        // the URI does not contain a query (breaks Note search segue)
                        !quorumLink.includes("?")
                    ) {
                        extraChar = "/"
                    }

                    inlineProps.segueToUrl(`${feedItemUrl}${extraChar}`)
                }
            }}
            actionCreators={{
                // FeedItem objects can theoretically represent any underlying inline
                segueToEditBill,
                segueToEditComment,
                segueToEditContact,
                segueToEditCustomOfficial,
                segueToEditDonation,
                segueToEditEvent,
                segueToEditInteraction,
                segueToEditNote,
                segueToEditOfficial,
                segueToEditOrganization,
                segueToEditRegulation,
                segueToEditRelationship,
                segueToOutboxCompose,
                segueToOutboxComposeSupporter,

                setOrCreatePriority,
                setOrCreateStance,
                postProfileAlignment,

                deleteContact,
                deleteEvent,
                deleteFile,
                deleteGrassrootsAction,
                deleteNote,

                updateInlineAppearance,
                updateSupporterAction,

                createNewTemplateFromSearch,
            }}
            {...props}
        />
    )
}

export const BulkUploadFileInline = (props) => (
    <GenericInline
        formatSelect={bulkUploadFileInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.bulk_upload}
        {...props}
    />
)

export const CustomEventParticipantInline = (props) => (
    <GenericInline
        formatSelect={customEventParticipantInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.custom_event_participant}
        renderCustomFields
        {...props}
    />
)

/* ****************************************************************************
 * Generic Selectors
 ***************************************************************************** */
export const EventParticipantInline = (props) => (
    <GenericInline
        formatSelect={eventParticipantInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const LiveTranscriptSectionInline = (props) => (
    <GenericInline
        formatSelect={liveTranscriptSectionInlineSelector}
        presentationComponent={GenericInlinePresentation}
        selectOptions={liveTranscriptSectionOptionsSelector}
        {...props}
    />
)

export const BoardInline = (props) => (
    <GenericInline
        formatSelect={genericSelectorCreator({
            iconName: "map",
            firstField: "name",
            secondField: "creator_username",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const StateInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "name",
            secondField: "capital",
            thirdField: "abbrev",
            stateAbbrevField: "abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const DistrictInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "name",
            secondField: "member_display_name",
            stateAbbrevField: "state_abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const UpperLDInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "kw_Description",
            secondField: "member_display_name",
            stateAbbrevField: "state_abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const LowerLDInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "kw_Description",
            secondField: "member_display_name",
            stateAbbrevField: "state_abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const CountyInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "name",
            stateAbbrevField: "state_abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const MunicipalityInline = (props) => (
    <GenericInline
        formatSelect={genericStateSelectorCreator({
            firstField: "name",
            stateAbbrevField: "state_abbrev",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const CountryInline = (props) => (
    <GenericInline
        formatSelect={genericSelectorCreator({
            iconName: "globe",
            firstField: "name",
            secondField: "capital",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const GeoShapeInline = (props) => (
    <GenericInline formatSelect={geoshapeInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const CustomRegionInline = (props) => (
    <GenericInline
        formatSelect={genericSelectorCreator({
            iconName: "flag-o",
            firstField: "category",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const CustomDataInline = (props) => (
    <GenericInline
        formatSelect={customDataInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const BulkEmailInline = (props) => (
    <GenericInline
        formatSelect={bulkEmailInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.bulk_email}
        {...props}
    />
)

export const SendEmailInline = (props) => (
    <GenericInline
        formatSelect={sendEmailInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)
export const SendSMSInline = (props) => (
    <GenericInline formatSelect={sendSMSInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const ReplyEmailInline = (props) => (
    <GenericInline
        formatSelect={replyEmailInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const BulkSMSInline = (props) => (
    <GenericInline
        formatSelect={bulkSMSInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.bulk_sms}
        {...props}
    />
)

// need to duplicate this logic for now for mobile sanity but will want to
// seg to outbox profile from here
export const SendEmailInlineInProfile = (props) => (
    <GenericInline
        formatSelect={sendEmailInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const LegislativeSessionInline = (props) => (
    <GenericInline
        formatSelect={legislativeSessionInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const CampaignInline = (props) => (
    <GenericInline
        formatSelect={campaignInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.campaign}
        {...props}
    />
)

export const ActionCenterSettingsInline = (props) => (
    <GenericInline
        formatSelect={actionCenterSettingsInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.action_center_settings}
        {...props}
    />
)

export const WitnessInline = (props) => (
    <GenericInline
        formatSelect={genericSelectorCreator({
            iconName: "user-circle-o",
            firstField: "name",
            secondField: "organization",
            thirdField: "honorific",
        })}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const StafferTitleInline = (props) => (
    <GenericInline
        formatSelect={stafferTitleInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const StafferTermInline = (props) => (
    <GenericInline
        formatSelect={stafferTermInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

export const IssueInline = (props) => (
    <GenericInline formatSelect={issueInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const StatInline = (props) => (
    <GenericInline formatSelect={statInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const TeamInline = (props) => (
    <GenericInline formatSelect={teamInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const OrganizationInline = (props) => (
    <GenericInline
        formatSelect={publicOrganizationInlineSelector}
        presentationComponent={GenericInlinePresentation}
        {...props}
    />
)

// this exists on mobile, but not on web
export const AssignmentInline = (props) => (
    <GenericInline
        formatSelect={assignmentInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.assignment}
        {...props}
    />
)

export const SavedSearchInline = (props) => <div />

export const AgencyInline = (props) => (
    <GenericInline formatSelect={agencyInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)

export const DocketInline = (props) => (
    <GenericInline
        formatSelect={docketInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.docket}
        selectOptions={docketOptionsSelector}
        actionCreators={{
            setOrCreatePriority,
            setOrCreateStance,
        }}
        {...props}
    />
)

export const TrackingDashboardInline = (props) => (
    <GenericInline
        formatSelect={trackingDashboardInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.tracking_dashboard}
        {...props}
    />
)

export const ArticleInline = (props) => (
    <GenericInline
        alwaysHasExpandControl
        formatSelect={articleInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.media}
        selectOptions={articleOptionsSelector}
        actionCreators={{
            setOrCreatePriority,
            setOrCreateStance,
        }}
        {...props}
    />
)

export const ArticleTopicInline = (props) => (
    <GenericInline
        formatSelect={articleTopicInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.media_topic}
        {...props}
    />
)

export const PoliticalCommitteeInline = (props) => (
    <GenericInline
        formatSelect={politicalCommitteeInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.political_committee}
        selectOptions={politicalCommitteeOptionsSelector}
        actionCreators={{
            segueToEditPoliticalCommittee,
        }}
        {...props}
    />
)

export const TransactionInline = (props) => (
    <GenericInline
        formatSelect={transactionInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.transaction}
        selectOptions={transactionOptionsSelector}
        customOnPress={(_id, inlineProps) => {
            const editTransactionUrl = getTransactionEditUrl({
                id: props.datum.get("id"),
                ledger_settings_id: props.datum.get("ledger_settings_id"),
            })
            inlineProps.segueToUrl(editTransactionUrl)
        }}
        {...props}
    />
)

export const PacClassicPacOfficeInline = (props) => (
    <GenericInline
        formatSelect={pacClassicPacOfficeInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.pacc_pac_office}
        {...props}
    />
)

export const PacClassicPacOrganizationInline = (props) => (
    <GenericInline
        formatSelect={pacClassicPacOrganizationInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.pacc_pac_organization}
        {...props}
    />
)

export const PacClassicPacTransactionInline = (props) => (
    <GenericInline
        formatSelect={pacClassicPacTransactionInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.pacc_pac_transaction}
        selectOptions={pacClassicPacTransactionOptionsSelector}
        customOnPress={(id, inlineProps) => {
            inlineProps.segueToUrl(paths.transaction.replace(":id", id))
        }}
        {...props}
    />
)

export const CampaignFinanceReceiptInline = (props) => (
    <GenericInline
        formatSelect={campaignFinanceReceiptInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.receipt}
        selectOptions={campaignFinanceReceiptOptionsSelector}
        customOnPress={(id, inlineProps) => {
            const pdfUrl = `${RECEIPT_ENDPOINT}/${id}.pdf`

            axios
                .head(pdfUrl)
                .then(() => window.open(pdfUrl, "_blank"))
                .catch(() =>
                    swal({
                        title: "Error",
                        text: "The original filing appears to be unavailable",
                        icon: "error",
                    }),
                )
        }}
        {...props}
    />
)

export const CampaignFinanceReceiptDisabledInline = (props) => (
    <GenericInline
        formatSelect={campaignFinanceReceiptDisabledInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.receipt}
        selectOptions={campaignFinanceReceiptOptionsSelector}
        customOnPress={(id, inlineProps) => {
            const pdfUrl = `${RECEIPT_ENDPOINT}/${id}.pdf`

            axios
                .head(pdfUrl)
                .then(() => window.open(pdfUrl, "_blank"))
                .catch(() =>
                    swal({
                        title: "Error",
                        text: "The original filing appears to be unavailable",
                        icon: "error",
                    }),
                )
        }}
        {...props}
    />
)

export const CampaignFinanceDisbursementInline = (props) => (
    <GenericInline
        formatSelect={campaignFinanceDisbursementInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.disbursement}
        selectOptions={campaignFinanceDisbursementOptionsSelector}
        customOnPress={(id, inlineProps) => {
            const pdfUrl = `${DISBURSEMENT_ENDPOINT}/${id}.pdf`

            axios
                .head(pdfUrl)
                .then(() => window.open(pdfUrl, "_blank"))
                .catch(() =>
                    swal({
                        title: "Error",
                        text: "The original filing appears to be unavailable",
                        icon: "error",
                    }),
                )
        }}
        {...props}
    />
)

export const CampaignFinanceDisbursementDisabledInline = (props) => (
    <GenericInline
        formatSelect={campaignFinanceDisbursementDisabledInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.disbursement}
        selectOptions={campaignFinanceDisbursementOptionsSelector}
        customOnPress={(id, inlineProps) => {
            const pdfUrl = `${DISBURSEMENT_ENDPOINT}/${id}.pdf`

            axios
                .head(pdfUrl)
                .then(() => window.open(pdfUrl, "_blank"))
                .catch(() =>
                    swal({
                        title: "Error",
                        text: "The original filing appears to be unavailable",
                        icon: "error",
                    }),
                )
        }}
        {...props}
    />
)

export const ElectionInline = (props) => (
    <GenericInline
        formatSelect={electionInlineSelector}
        presentationComponent={GenericInlinePresentation}
        quorumDataType={DjangIO.app.models.QuorumDataType.election}
        {...props}
    />
)

export const BeatInline = (props) => (
    <GenericInline formatSelect={beatInlineSelector} presentationComponent={GenericInlinePresentation} {...props} />
)
